import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  EmployeeInvitation,
  EmployeeInvitationList,
  FieldDefinition,
  FieldRequirement,
  Group,
  InvitationRegistrationList,
  LinkRequestList,
  Role,
  Site,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  Badge,
  Btn,
  Card,
  CardPad,
  Chip,
  Drawer,
  Empty,
  Field,
  IconBtn,
  Input,
  Modal,
  Note,
  Percent,
  Row,
  Sect,
  Segmented,
  Select,
  Table,
  useAdminToast,
  type BadgeTone,
} from '../admin-kit';
import { ConfirmModal } from '../organisatie/confirm-modal';
import '../organisatie/organisatie.css';
import { saveBlob } from '../../shared/download';
import { formatDate, formatDateTime } from '../../shared/format';
import { ListError } from '../../shared/list-error';
import { useSession } from '../../shared/session';

const POSTER_LANGUAGES = ['nl', 'fr', 'en', 'pl'] as const;

/** De lijst van de uitnodigingen voor medewerkers; ook de teller op de tab gebruikt ze. */
export function useEmployeeInvitations() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['employee-invitations'],
    queryFn: async (): Promise<EmployeeInvitation[]> => {
      const { data, error } = await api.GET('/api/v1/invitations/employees', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as EmployeeInvitationList).items;
    },
  });
}

/**
 * Het tabblad "Medewerkers" op `/admin/invitations` (Stap 34, REG-2; ontwerp O9 en O10).
 *
 * Een lijst met de stand ("12 van 50"), en per uitnodiging de QR, de poster, de link, de
 * registraties en intrekken. Bewerken bestaat niet: intrekken en een nieuwe maken is de weg
 * (REG-2). De knop "Nieuwe uitnodiging" staat in de paginakop; `creating` zegt of het paneel open is.
 */
export function EmployeeInvitationsTab({
  creating,
  onCreatingChange,
}: {
  creating: boolean;
  onCreatingChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const adminToast = useAdminToast();
  const [qr, setQr] = useState<EmployeeInvitation | null>(null);
  const [poster, setPoster] = useState<EmployeeInvitation | null>(null);
  const [registraties, setRegistraties] = useState<EmployeeInvitation | null>(null);
  const [intrekken, setIntrekken] = useState<EmployeeInvitation | null>(null);

  const list = useEmployeeInvitations();

  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await api.POST('/api/v1/invitations/employees/{invitationId}/revoke', {
        params: { path: { invitationId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setIntrekken(null);
      await queryClient.invalidateQueries({ queryKey: ['employee-invitations'] });
      adminToast.show(t('employeeInvitations.revoked'));
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: t('errors.generic'),
        description: problemDetail(error) ?? undefined,
      }),
  });

  /** Actief groen, Vol oranje, Verlopen en Ingetrokken grijs (O9). */
  const statusTone: Record<EmployeeInvitation['status'], BadgeTone> = {
    open: 'ok',
    full: 'attention',
    expired: 'neutral',
    revoked: 'neutral',
  };

  const meta = (invitation: EmployeeInvitation) =>
    invitation.kind === 'person'
      ? invitation.personName
        ? t('beheer.organisatie.invitations.metaPerson', { name: invitation.personName })
        : t('beheer.organisatie.invitations.metaPersonUnknown')
      : invitation.code
        ? t('beheer.organisatie.invitations.metaOpen', { code: invitation.code })
        : t('beheer.organisatie.invitations.metaOpenNoCode');

  const kopieer = (invitation: EmployeeInvitation) => {
    void navigator.clipboard?.writeText(invitation.link ?? '');
    adminToast.show(t('employeeInvitations.copied'));
  };

  return (
    <>
      <LinkRequestsCard />
      <Card>
        {list.isPending ? (
          <CardPad>
            <LoadingState rows={3} label={t('common.loading')} />
          </CardPad>
        ) : list.isError ? (
          <CardPad>
            <ListError onRetry={() => void list.refetch()} error={list.error} />
          </CardPad>
        ) : (list.data ?? []).length === 0 ? (
          <Empty
            icon={<AdminIcon name="qr" size={24} />}
            title={t('employeeInvitations.emptyTitle')}
            text={t('employeeInvitations.emptyHint')}
          />
        ) : (
          <Table label={t('employeeInvitations.title')}>
            <thead>
              <tr>
                <th>{t('employeeInvitations.name')}</th>
                <th>{t('employeeInvitations.destination')}</th>
                <th>{t('employeeInvitations.status')}</th>
                <th>{t('employeeInvitations.used')}</th>
                <th>{t('employeeInvitations.validUntil')}</th>
                <th className="act" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((invitation) => {
                const actief = invitation.status === 'open';
                return (
                  <tr key={invitation.id} data-testid="employee-invitation-row">
                    <td>
                      <div className="a-name">{invitation.name}</div>
                      <div className="a-meta">{meta(invitation)}</div>
                    </td>
                    <td>{invitation.destination || '—'}</td>
                    <td>
                      <Badge tone={statusTone[invitation.status]}>
                        {invitation.status === 'full'
                          ? t('beheer.organisatie.invitations.statusFull')
                          : t(`employeeInvitations.statusValue.${invitation.status}`)}
                      </Badge>
                    </td>
                    <td>
                      {invitation.maxUses ? (
                        <Percent
                          value={(invitation.usedCount / invitation.maxUses) * 100}
                          label={t('beheer.organisatie.invitations.usedOf', {
                            used: invitation.usedCount,
                            max: invitation.maxUses,
                          })}
                        />
                      ) : (
                        t('employeeInvitations.usedCount', { used: invitation.usedCount })
                      )}
                    </td>
                    <td>{formatDate(invitation.expiresAt)}</td>
                    <ActionsCell>
                      {actief ? (
                        <>
                          <IconBtn
                            label={t('beheer.organisatie.invitations.showQr')}
                            icon={<AdminIcon name="qr" />}
                            testId="invitation-qr"
                            onClick={() => setQr(invitation)}
                          />
                          <IconBtn
                            label={t('employeeInvitations.poster')}
                            icon={<AdminIcon name="printer" />}
                            testId="invitation-poster"
                            onClick={() => setPoster(invitation)}
                          />
                          <IconBtn
                            label={t('beheer.organisatie.invitations.copyLink')}
                            icon={<AdminIcon name="copy" />}
                            testId="invitation-copy"
                            onClick={() => kopieer(invitation)}
                          />
                        </>
                      ) : null}
                      <IconBtn
                        label={t('employeeInvitations.registrations')}
                        icon={<AdminIcon name="groups" />}
                        testId="invitation-registrations"
                        onClick={() => setRegistraties(invitation)}
                      />
                      {actief && has('identity.invitation.revoke') ? (
                        <Btn
                          variant="ghost"
                          size="sm"
                          testId="employee-invitation-revoke"
                          onClick={() => setIntrekken(invitation)}
                        >
                          {t('employeeInvitations.revoke')}
                        </Btn>
                      ) : null}
                    </ActionsCell>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {creating ? <NewInvitationDrawer onClose={() => onCreatingChange(false)} /> : null}
      {qr ? <QrModal invitation={qr} onClose={() => setQr(null)} /> : null}
      {poster ? <PosterModal invitation={poster} onClose={() => setPoster(null)} /> : null}
      {registraties ? (
        <RegistrationsModal invitation={registraties} onClose={() => setRegistraties(null)} />
      ) : null}

      <ConfirmModal
        open={intrekken !== null}
        onClose={() => setIntrekken(null)}
        title={t('employeeInvitations.revoke')}
        description={t('employeeInvitations.revokeConfirm')}
        confirmLabel={t('employeeInvitations.revoke')}
        busy={revoke.isPending}
        testId="confirm-employee-invitation-revoke"
        onConfirm={() => intrekken && revoke.mutate(intrekken.id)}
      />

      {adminToast.element}
    </>
  );
}

/** De QR-code als plaatje; de server tekent hem, zodat het plaatje en de poster gelijk zijn. */
/**
 * Registraties op een bestaand personeelsnummer die op HR wachten (SEC-5).
 *
 * Alleen zichtbaar als er iets wacht: naast elkaar wie er in de lijst staat en wat de aanvrager
 * intypte, zodat HR kan zien of het dezelfde persoon is voor er een account bestaat.
 */
function LinkRequestsCard() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const adminToast = useAdminToast();

  const requests = useQuery({
    queryKey: ['employee-invitations', 'link-requests'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/invitations/employees/link-requests', {});
      if (error) throw error;
      return (data as LinkRequestList).items;
    },
  });

  const decide = useMutation({
    mutationFn: async (input: { requestId: string; approve: boolean }) => {
      const path = input.approve
        ? '/api/v1/invitations/employees/link-requests/{requestId}/approve'
        : '/api/v1/invitations/employees/link-requests/{requestId}/reject';
      const { error } = await api.POST(path, { params: { path: { requestId: input.requestId } } });
      if (error) throw error;
      return input.approve;
    },
    onSuccess: async (approved) => {
      await queryClient.invalidateQueries({ queryKey: ['employee-invitations'] });
      adminToast.show(
        t(
          approved
            ? 'employeeInvitations.linkRequests.approved'
            : 'employeeInvitations.linkRequests.rejected',
        ),
      );
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: t('errors.generic'),
        description: problemDetail(error) ?? undefined,
      }),
  });

  const items = requests.data ?? [];
  if (items.length === 0) return adminToast.element;
  const mayDecide = has('identity.invitation.create');

  return (
    <>
      <Card>
        <CardPad>
          <div className="a-name">{t('employeeInvitations.linkRequests.title')}</div>
          <div className="a-meta">{t('employeeInvitations.linkRequests.intro')}</div>
        </CardPad>
        <Table label={t('employeeInvitations.linkRequests.title')} testId="link-requests">
          <thead>
            <tr>
              <th>{t('employeeInvitations.linkRequests.person')}</th>
              <th>{t('employeeInvitations.linkRequests.typed')}</th>
              <th>{t('employeeInvitations.linkRequests.invitation')}</th>
              <th>{t('employeeInvitations.linkRequests.when')}</th>
              <th className="act" aria-label={t('common.actions')} />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid="link-request-row">
                <td>
                  <div className="a-name">{item.personName}</div>
                  <div className="a-meta">{item.employeeNumber ?? '—'}</div>
                </td>
                <td>{item.typedName}</td>
                <td>{item.invitationName || '—'}</td>
                <td>{formatDateTime(item.createdAt)}</td>
                <ActionsCell>
                  {mayDecide ? (
                    <>
                      <Btn
                        size="sm"
                        testId="link-request-approve"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ requestId: item.id, approve: true })}
                      >
                        {t('employeeInvitations.linkRequests.approve')}
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        testId="link-request-reject"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ requestId: item.id, approve: false })}
                      >
                        {t('employeeInvitations.linkRequests.reject')}
                      </Btn>
                    </>
                  ) : null}
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      {adminToast.element}
    </>
  );
}

function useQrImage(invitationId: string): string | null {
  const { api } = useSession();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    void (async () => {
      const { data } = await api.GET('/api/v1/invitations/employees/{invitationId}/qr', {
        params: { path: { invitationId }, query: { format: 'png' } },
        parseAs: 'blob',
      });
      if (data) {
        objectUrl = URL.createObjectURL(data as unknown as Blob);
        setUrl(objectUrl);
      }
    })();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, invitationId]);

  return url;
}

/** QR-code, code en link van een uitnodiging: in het venster en na het maken. */
function QrBlock({ invitation }: { invitation: EmployeeInvitation }) {
  const { t } = useTranslation();
  const url = useQrImage(invitation.id);
  return (
    <div className="flex flex-col gap-[14px]" data-testid="invitation-qr-dialog">
      {url ? (
        <img src={url} alt="" className="org-qr" />
      ) : (
        <LoadingState rows={1} label={t('common.loading')} />
      )}
      {invitation.code ? (
        <div className="a-code">
          <span className="a-sr">{t('beheer.organisatie.invitations.code')}: </span>
          {invitation.code}
        </div>
      ) : null}
      {invitation.link ? (
        <Field label={t('beheer.organisatie.invitations.link')}>
          {(props) => <Input {...props} readOnly value={invitation.link ?? ''} />}
        </Field>
      ) : null}
    </div>
  );
}

function QrModal({ invitation, onClose }: { invitation: EmployeeInvitation; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      title={invitation.name}
      footer={
        <Btn variant="sec" onClick={onClose}>
          {t('common.close')}
        </Btn>
      }
    >
      <QrBlock invitation={invitation} />
    </Modal>
  );
}

/** Een hoofdtaal en optioneel een tweede (besluit van 7 oktober 2026). */
function PosterModal({
  invitation,
  onClose,
}: {
  invitation: EmployeeInvitation;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const [lang, setLang] = useState('nl');
  const [lang2, setLang2] = useState('');

  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.GET('/api/v1/invitations/employees/{invitationId}/poster', {
        params: {
          path: { invitationId: invitation.id },
          query: lang2 ? { lang, lang2 } : { lang },
        },
        parseAs: 'blob',
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await saveBlob(data as unknown as Blob, `poster-${invitation.code ?? invitation.id}.pdf`);
    },
    onSuccess: onClose,
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: t('errors.generic'),
        description: problemDetail(error) ?? undefined,
      }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('employeeInvitations.posterTitle')}
      sub={invitation.name}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            icon={<AdminIcon name="printer" />}
            testId="poster-download"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            {t('employeeInvitations.download')}
          </Btn>
        </>
      }
    >
      <Row>
        <Field label={t('employeeInvitations.posterLanguage')}>
          {(props) => (
            <Select {...props} value={lang} onChange={(event) => setLang(event.target.value)}>
              {POSTER_LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {t(`employeeInvitations.language.${code}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('employeeInvitations.posterSecond')}>
          {(props) => (
            <Select
              {...props}
              value={lang2}
              onChange={(event) => setLang2(event.target.value)}
              data-testid="poster-lang2"
            >
              <option value="">{t('employeeInvitations.noSecond')}</option>
              {POSTER_LANGUAGES.filter((code) => code !== lang).map((code) => (
                <option key={code} value={code}>
                  {t(`employeeInvitations.language.${code}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Row>
    </Modal>
  );
}

function RegistrationsModal({
  invitation,
  onClose,
}: {
  invitation: EmployeeInvitation;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const list = useQuery({
    queryKey: ['employee-invitations', invitation.id, 'registrations'],
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/api/v1/invitations/employees/{invitationId}/registrations',
        {
          params: { path: { invitationId: invitation.id } },
        },
      );
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as InvitationRegistrationList).items;
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('employeeInvitations.registrations')}
      sub={invitation.name}
      footer={
        <Btn variant="sec" onClick={onClose}>
          {t('common.close')}
        </Btn>
      }
    >
      {list.isPending ? (
        <LoadingState rows={3} label={t('common.loading')} />
      ) : list.isError ? (
        <ListError onRetry={() => void list.refetch()} error={list.error} />
      ) : (list.data ?? []).length === 0 ? (
        <Empty
          icon={<AdminIcon name="groups" size={24} />}
          title={t('employeeInvitations.noRegistrations')}
        />
      ) : (
        <ul className="flex flex-col" data-testid="invitation-registration-list">
          {(list.data ?? []).map((item) => (
            <li key={item.personId} className="a-switch">
              <div className="a-switch-text">
                <div className="a-switch-title">{item.name}</div>
              </div>
              <span className="a-meta">{formatDateTime(item.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/** Vandaag plus `days` dagen, als `yyyy-mm-dd` voor een datumveld. */
function isoDate(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Hoeveel dagen een uitnodiging geldt tot en met die datum; de server vraagt dagen (1-365). */
function daysUntil(value: string): number | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const doel = new Date(year, month - 1, day);
  const vandaag = new Date();
  vandaag.setHours(0, 0, 0, 0);
  return Math.round((doel.getTime() - vandaag.getTime()) / 86_400_000);
}

/**
 * "Nieuwe uitnodiging" (O10): soort, naam, wat de nieuwe medewerker krijgt (rol, vestiging,
 * groepen), geldigheid en maximum, en per gegevensveld Niet / Vragen / Verplicht.
 *
 * Na het maken toont het paneel de QR-code, de code, de link en de poster - zoals de
 * zelfregistratie-spec het vraagt - in plaats van meteen te sluiten.
 */
function NewInvitationDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [kind, setKind] = useState<'open' | 'person'>('open');
  const [name, setName] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [personId, setPersonId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [groepKiezen, setGroepKiezen] = useState(false);
  const [siteId, setSiteId] = useState('');
  const [validUntil, setValidUntil] = useState(isoDate(30));
  const [maxUses, setMaxUses] = useState('');
  const [required, setRequired] = useState<Record<string, FieldRequirement | ''>>({
    employee_number: 'required',
  });
  const [gemaakt, setGemaakt] = useState<EmployeeInvitation | null>(null);
  const [poster, setPoster] = useState(false);

  const groups = useQuery({
    queryKey: ['groups', 'all'],
    queryFn: async (): Promise<Group[]> => {
      const { data } = await api.GET('/api/v1/groups', {});
      return (data as { items: Group[] } | undefined)?.items ?? [];
    },
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async (): Promise<Role[]> => {
      const { data } = await api.GET('/api/v1/roles', {});
      return (data as { roles: Role[] } | undefined)?.roles ?? [];
    },
  });
  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: async (): Promise<Site[]> => {
      const { data } = await api.GET('/api/v1/sites', {});
      return (data as { items: Site[] } | undefined)?.items ?? [];
    },
  });
  const fields = useQuery({
    queryKey: ['fields'],
    queryFn: async (): Promise<FieldDefinition[]> => {
      const { data } = await api.GET('/api/v1/fields', {});
      return ((data as { items: FieldDefinition[] } | undefined)?.items ?? []).filter(
        (field) => field.archivedAt === null,
      );
    },
  });
  const people = useQuery({
    queryKey: ['people', 'search', personSearch],
    enabled: kind === 'person' && personSearch.trim().length >= 2,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: personSearch, limit: 10 } as never },
      });
      return (
        (
          data as
            | {
                items: {
                  id: string;
                  firstName: string;
                  lastName: string;
                  employeeNumber: string | null;
                }[];
              }
            | undefined
        )?.items ?? []
      );
    },
  });

  const validDays = daysUntil(validUntil);
  const geldigheidOk = validDays !== null && validDays >= 1 && validDays <= 365;

  const create = useMutation({
    mutationFn: async () => {
      const requiredFields = Object.fromEntries(
        Object.entries(required).filter(([, value]) => value !== ''),
      ) as Record<string, FieldRequirement>;
      const { data, error } = await api.POST('/api/v1/invitations/employees', {
        body: {
          kind,
          name: name.trim(),
          personId: kind === 'person' ? personId : null,
          prefill: { roleId: roleId || null, groupIds, siteId: siteId || null, personType: null },
          requiredFields:
            kind === 'person'
              ? Object.fromEntries(
                  Object.entries(requiredFields).filter(([key]) => key !== 'employee_number'),
                )
              : requiredFields,
          validDays,
          maxUses: kind === 'open' && maxUses ? Number(maxUses) : null,
        } as never,
      });
      if (error) throw error;
      return (data as EmployeeInvitation | undefined) ?? null;
    },
    onSuccess: async (invitation) => {
      await queryClient.invalidateQueries({ queryKey: ['employee-invitations'] });
      toast.show({ tone: 'success', title: t('employeeInvitations.created') });
      if (invitation) setGemaakt(invitation);
      else onClose();
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: t('errors.generic'),
        description: problemDetail(error) ?? undefined,
      }),
  });

  if (gemaakt) {
    return (
      <>
        <Drawer
          open
          onClose={onClose}
          title={t('beheer.organisatie.invitations.readyTitle')}
          sub={gemaakt.name}
          footer={
            <>
              {gemaakt.link ? (
                <Btn
                  variant="sec"
                  icon={<AdminIcon name="copy" />}
                  onClick={() => void navigator.clipboard?.writeText(gemaakt.link ?? '')}
                >
                  {t('beheer.organisatie.invitations.copyLink')}
                </Btn>
              ) : null}
              <Btn
                variant="sec"
                icon={<AdminIcon name="printer" />}
                testId="invitation-created-poster"
                onClick={() => setPoster(true)}
              >
                {t('employeeInvitations.poster')}
              </Btn>
              <Btn onClick={onClose}>{t('beheer.organisatie.invitations.done')}</Btn>
            </>
          }
        >
          <Note tone="info">{t('beheer.organisatie.invitations.readyText')}</Note>
          <QrBlock invitation={gemaakt} />
        </Drawer>
        {poster ? <PosterModal invitation={gemaakt} onClose={() => setPoster(false)} /> : null}
      </>
    );
  }

  const requirementSeg = (key: string, label: string) => (
    <tr key={key} data-testid={`requirement-${key}`}>
      <td>{label}</td>
      <td>
        <Segmented
          label={label}
          value={required[key] || 'none'}
          onChange={(value) =>
            setRequired((current) => ({
              ...current,
              [key]: value === 'none' ? '' : (value as FieldRequirement),
            }))
          }
          options={(['none', 'ask', 'required'] as const).map((value) => ({
            value,
            label: t(`beheer.organisatie.invitations.requirement.${value}`),
          }))}
        />
      </td>
    </tr>
  );

  const gekozenGroepen = (groups.data ?? []).filter((group) => groupIds.includes(group.id));
  const overigeGroepen = (groups.data ?? []).filter((group) => !groupIds.includes(group.id));
  const kanMaken =
    name.trim().length >= 2 && (kind !== 'person' || personId !== '') && geldigheidOk;

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('employeeInvitations.new')}
      sub={t('beheer.organisatie.invitations.newSub')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            icon={<AdminIcon name="qr" />}
            testId="employee-invitation-save"
            disabled={create.isPending || !kanMaken}
            onClick={() => create.mutate()}
          >
            {t('beheer.organisatie.invitations.create')}
          </Btn>
        </>
      }
    >
      <div className="a-field">
        <div className="a-label" id="uitnodiging-soort">
          {t('employeeInvitations.kind')}
        </div>
        <Segmented
          labelledBy="uitnodiging-soort"
          value={kind}
          onChange={setKind}
          options={[
            { value: 'open', label: t('beheer.organisatie.invitations.kindOpen') },
            { value: 'person', label: t('beheer.organisatie.invitations.kindPerson') },
          ]}
        />
      </div>
      <Field
        label={t('employeeInvitations.name')}
        hint={t('beheer.organisatie.invitations.nameHint')}
      >
        {(props) => (
          <Input
            {...props}
            placeholder={t('employeeInvitations.namePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="employee-invitation-name"
          />
        )}
      </Field>
      {kind === 'person' ? (
        <Row>
          <Field label={t('employeeInvitations.person')}>
            {(props) => (
              <Input
                {...props}
                type="search"
                value={personSearch}
                onChange={(event) => setPersonSearch(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('employeeInvitations.choosePerson')}>
            {(props) => (
              <Select
                {...props}
                value={personId}
                onChange={(event) => setPersonId(event.target.value)}
              >
                <option value="">—</option>
                {(people.data ?? []).map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                    {person.employeeNumber ? ` (${person.employeeNumber})` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </Row>
      ) : null}

      <Sect title={t('beheer.organisatie.invitations.sectGets')}>
        <Row>
          <Field
            label={t('employeeInvitations.role')}
            hint={t('beheer.organisatie.invitations.roleHint')}
          >
            {(props) => (
              <Select {...props} value={roleId} onChange={(event) => setRoleId(event.target.value)}>
                <option value="">{t('employeeInvitations.defaultRole')}</option>
                {(roles.data ?? []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name.nl ?? Object.values(role.name)[0]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {(sites.data ?? []).length > 0 ? (
            <Field label={t('employeeInvitations.site')}>
              {(props) => (
                <Select
                  {...props}
                  value={siteId}
                  onChange={(event) => setSiteId(event.target.value)}
                >
                  <option value="">{t('beheer.organisatie.invitations.noSite')}</option>
                  {(sites.data ?? []).map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
        </Row>
        <div className="a-field">
          <div className="a-label" id="uitnodiging-groepen">
            {t('employeeInvitations.groups')}
          </div>
          <div className="a-actions" role="group" aria-labelledby="uitnodiging-groepen">
            {gekozenGroepen.map((group) => (
              <Chip
                key={group.id}
                onRemove={() => setGroupIds((current) => current.filter((id) => id !== group.id))}
              >
                {group.name}
              </Chip>
            ))}
            {overigeGroepen.length > 0 ? (
              <Btn
                variant="ghost"
                size="sm"
                icon={<AdminIcon name="plus" />}
                aria-expanded={groepKiezen}
                onClick={() => setGroepKiezen((huidig) => !huidig)}
              >
                {t('beheer.organisatie.invitations.addGroup')}
              </Btn>
            ) : null}
          </div>
          {/* De groepen om uit te kiezen, onder de chips: een lijst van tientallen past niet in een menu. */}
          {groepKiezen && overigeGroepen.length > 0 ? (
            <Select
              aria-label={t('beheer.organisatie.invitations.addGroup')}
              value=""
              data-testid="employee-invitation-group"
              onChange={(event) => {
                const id = event.target.value;
                if (id) setGroupIds((current) => [...current, id]);
                setGroepKiezen(false);
              }}
            >
              <option value="">—</option>
              {overigeGroepen.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          ) : null}
        </div>
      </Sect>

      <Row>
        <Field
          label={t('beheer.organisatie.invitations.validUntil')}
          error={geldigheidOk ? undefined : t('beheer.organisatie.invitations.validUntilError')}
        >
          {(props) => (
            <Input
              {...props}
              type="date"
              min={isoDate(1)}
              max={isoDate(365)}
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
            />
          )}
        </Field>
        {kind === 'open' ? (
          <Field
            label={t('beheer.organisatie.invitations.maxUses')}
            hint={t('beheer.organisatie.invitations.maxUsesHint')}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                placeholder={t('employeeInvitations.unlimited')}
                value={maxUses}
                onChange={(event) => setMaxUses(event.target.value)}
                data-testid="employee-invitation-max"
              />
            )}
          </Field>
        ) : null}
      </Row>

      <Sect title={t('beheer.organisatie.invitations.sectFields')}>
        <p className="a-hint">{t('beheer.organisatie.invitations.fieldsHint')}</p>
        <Table label={t('beheer.organisatie.invitations.sectFields')}>
          <thead>
            <tr>
              <th>{t('beheer.organisatie.invitations.colField')}</th>
              <th>{t('beheer.organisatie.invitations.colAsk')}</th>
            </tr>
          </thead>
          <tbody>
            {kind === 'open'
              ? requirementSeg('employee_number', t('employeeInvitations.employeeNumber'))
              : null}
            {(fields.data ?? []).map((field) =>
              requirementSeg(field.key, field.label.nl ?? field.key),
            )}
          </tbody>
        </Table>
      </Sect>
    </Drawer>
  );
}
